import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Users, TrendingUp, TrendingDown, Clock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { useSort, SortHead } from "../components/SortHead";
import api, { fmtNum, fmtDate } from "../lib/api";

const MONTHS_HR = ["Sij", "Velj", "Ožu", "Tra", "Svi", "Lip", "Srp", "Kol", "Ruj", "Lis", "Stu", "Pro"];
const yearOptions = () => {
  const y = new Date().getFullYear();
  return [y - 2, y - 1, y, y + 1];
};

const delayLabel = (d) => {
  if (d === null || d === undefined) return null;
  if (Math.abs(d) < 0.5) return "na vrijeme";
  return d > 0 ? `+${d} dana kasni` : `${Math.abs(d)} dana ranije`;
};

const TopList = ({ title, icon: Icon, rows, total, color, testid }) => (
  <div className="bg-white border border-[#E2E8F0] rounded-sm p-5" data-testid={testid}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className="w-4 h-4" style={{ color }} />
      <h2 className="font-heading text-lg font-semibold text-[#0F172A]">{title}</h2>
    </div>
    <p className="text-xs text-[#64748B] mb-4">Ukupno: {fmtNum(total, "€")}</p>
    {rows.length === 0 && <p className="text-sm text-[#64748B] py-4">Nema podataka za odabrano razdoblje.</p>}
    <div className="space-y-2.5">
      {rows.map((r, i) => (
        <div key={r.name}>
          <div className="flex items-baseline justify-between gap-2 text-sm">
            <p className="truncate font-medium text-[#0F172A]">{i + 1}. {r.name}</p>
            <p className="tabular-nums shrink-0 text-[#475569]">{fmtNum(r.total_eur, "€")} · <span className="font-semibold" style={{ color }}>{r.share}%</span></p>
          </div>
          <div className="h-1.5 bg-[#F1F5F9] rounded-full mt-1">
            <div className="h-1.5 rounded-full" style={{ width: `${Math.min(r.share, 100)}%`, backgroundColor: color }} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default function PartnersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [scope, setScope] = useState("year");
  const [partners, setPartners] = useState([]);
  const [top, setTop] = useState(null);
  const [internal, setInternal] = useState(null);
  const [mergeSuggestions, setMergeSuggestions] = useState(0);
  const [detail, setDetail] = useState(null);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");

  const filtered = partners.filter(
    (p) => (kind === "all" || p.kind === kind) && (!q || p.name.toLowerCase().includes(q.toLowerCase()))
  );
  const pAccessors = {
    name: (p) => p.name.toLowerCase(),
    kind: (p) => p.kind,
    paid: (p) => p.paid_eur,
    received: (p) => p.received_eur,
    open: (p) => p.open_planned_eur,
    delay: (p) => p.avg_delay_days,
    last: (p) => p.last_date,
  };
  const { sorted: sortedPartners, sort, toggle } = useSort(filtered, pAccessors);

  useEffect(() => {
    const params = scope === "year" ? { year } : {};
    api.get("/partners", { params }).then((r) => setPartners(r.data));
    api.get("/partners/top", { params: { year } }).then((r) => setTop(r.data));
    api.get("/partners/internal", { params }).then((r) => setInternal(r.data));
    api.get("/partner-aliases/suggestions").then((r) => setMergeSuggestions(r.data.length)).catch(() => {});
  }, [year, scope]);

  useEffect(() => {
    const name = searchParams.get("name");
    if (name) openDetail(name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = (name) => {
    api.get("/partners/detail", { params: { name, year } })
      .then((r) => setDetail(r.data))
      .catch(() => setDetail(null));
  };

  const closeDetail = () => {
    setDetail(null);
    if (searchParams.get("name")) setSearchParams({});
  };

  const chartData = detail?.monthly.map((m, i) => ({ label: MONTHS_HR[i], Naplaćeno: m.in, Plaćeno: m.out })) || [];

  return (
    <div className="space-y-6 fade-up" data-testid="partners-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-1">Kupci i dobavljači</p>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-[#0F172A]">Partneri</h1>
          <p className="text-xs text-[#64748B] mt-1">Isplate radnicima nisu uključene (stranica <a href="/radnici" className="underline underline-offset-2 hover:text-[#0D9488]">Radnici</a>), kao ni isplate osnivačima (Franjo Vučić, Robert Rožić) ni transferi između vlastitih VTZ računa — oni nisu kupci ni dobavljači.</p>
        </div>
        <div className="flex gap-2">
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger data-testid="partners-scope-select" className="w-36 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="year">Odabrana godina</SelectItem>
              <SelectItem value="all">Sve vrijeme</SelectItem>
            </SelectContent>
          </Select>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger data-testid="partners-year-select" className="w-28 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              {yearOptions().map((y) => <SelectItem key={y} value={String(y)}>{y}.</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {mergeSuggestions > 0 && (
        <a
          href="/postavke"
          data-testid="partners-merge-suggestions-banner"
          className="block bg-[#D97706]/10 border border-[#D97706]/40 rounded-sm px-4 py-2.5 text-sm text-[#0F172A] hover:bg-[#D97706]/15 transition-colors duration-200"
        >
          <span className="font-semibold">{mergeSuggestions} {mergeSuggestions === 1 ? "prijedlog" : mergeSuggestions < 5 ? "prijedloga" : "prijedloga"} spajanja sličnih partnera</span>
          {" "}— imena koja bi mogla biti ista firma. Potvrdite jednim klikom u Postavkama →
        </a>
      )}

      {top && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TopList title={`Top dobavljači ${year}.`} icon={TrendingDown} rows={top.suppliers} total={top.total_out} color="#DC2626" testid="top-suppliers" />
          <TopList title={`Top kupci ${year}.`} icon={TrendingUp} rows={top.customers} total={top.total_in} color="#0D9488" testid="top-customers" />
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-center bg-white border border-[#E2E8F0] rounded-sm p-3">
        <Input data-testid="partners-search-input" className="w-64" placeholder="Pretraži partnera po nazivu..." value={q} onChange={(e) => setQ(e.target.value)} />
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger data-testid="partners-kind-select" className="w-40 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Svi partneri</SelectItem>
            <SelectItem value="kupac">Kupci</SelectItem>
            <SelectItem value="dobavljač">Dobavljači</SelectItem>
          </SelectContent>
        </Select>
        <p className="ml-auto text-xs text-[#64748B]" data-testid="partners-count">{sortedPartners.length} partnera</p>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F1F5F9] hover:bg-[#F1F5F9]">
              <SortHead label="Partner" sortKey="name" sort={sort} onToggle={toggle} />
              <SortHead label="Vrsta" sortKey="kind" sort={sort} onToggle={toggle} className="w-24" />
              <SortHead label="Plaćeno €" sortKey="paid" sort={sort} onToggle={toggle} className="text-right" align="right" />
              <SortHead label="Naplaćeno €" sortKey="received" sort={sort} onToggle={toggle} className="text-right" align="right" />
              <SortHead label="Otvoreni planovi" sortKey="open" sort={sort} onToggle={toggle} className="text-right" align="right" />
              <SortHead label="Prosj. kašnjenje" sortKey="delay" sort={sort} onToggle={toggle} className="text-right" align="right" />
              <SortHead label="Zadnja aktivnost" sortKey="last" sort={sort} onToggle={toggle} className="text-right" align="right" />
            </TableRow>
          </TableHeader>
          <TableBody data-testid="partners-table-body">
            {partners.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-[#64748B] py-10">Nema partnera za odabrano razdoblje.</TableCell></TableRow>
            )}
            {partners.length > 0 && sortedPartners.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-[#64748B] py-10">Nema partnera za odabrane filtre.</TableCell></TableRow>
            )}
            {sortedPartners.map((p) => (
              <TableRow
                key={p.name}
                data-testid={`partner-row-${p.name}`}
                onClick={() => openDetail(p.name)}
                className="cursor-pointer hover:bg-[#F8FAFC] transition-colors duration-200"
              >
                <TableCell className="font-medium text-sm max-w-[260px] truncate">{p.name}</TableCell>
                <TableCell>
                  <Badge className={`rounded-sm text-xs font-medium hover:bg-inherit ${p.kind === "kupac" ? "bg-[#0D9488]/10 text-[#0D9488]" : "bg-[#F1F5F9] text-[#475569]"}`}>{p.kind}</Badge>
                </TableCell>
                <TableCell className="text-right tabular-nums text-sm text-[#DC2626]">{p.paid_eur ? `−${fmtNum(p.paid_eur)}` : "—"}</TableCell>
                <TableCell className="text-right tabular-nums text-sm text-[#0D9488]">{p.received_eur ? `+${fmtNum(p.received_eur)}` : "—"}</TableCell>
                <TableCell className="text-right text-sm">
                  {p.open_planned > 0 ? (
                    <span className={p.overdue > 0 ? "text-[#DC2626] font-semibold" : "text-[#D97706]"}>
                      {p.open_planned} ({fmtNum(p.open_planned_eur, "€")}){p.overdue > 0 ? ` · ${p.overdue} dospjelo` : ""}
                    </span>
                  ) : "—"}
                </TableCell>
                <TableCell className="text-right text-sm tabular-nums">
                  {p.avg_delay_days !== null ? (
                    <span className={p.avg_delay_days > 0 ? "text-[#D97706]" : "text-[#0D9488]"}>{delayLabel(p.avg_delay_days)}</span>
                  ) : <span className="text-[#64748B]">—</span>}
                </TableCell>
                <TableCell className="text-right text-sm text-[#475569]">{fmtDate(p.last_date)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {internal && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="internal-section">
          <div className="bg-white border border-[#E2E8F0] rounded-sm p-5" data-testid="internal-founders-table">
            <h2 className="font-heading text-lg font-semibold text-[#0F172A] mb-1">Isplate osnivačima</h2>
            <p className="text-xs text-[#64748B] mb-3">Kategorija "Osnivači" — nisu kupci ni dobavljači pa su izuzeti iz gornjih lista.</p>
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F1F5F9] hover:bg-[#F1F5F9]">
                  <TableHead>Osnivač</TableHead>
                  <TableHead className="text-right">Isplata</TableHead>
                  <TableHead className="text-right">Isplaćeno €</TableHead>
                  <TableHead className="text-right">Uplaćeno €</TableHead>
                  <TableHead className="text-right">Zadnja</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {internal.founders.map((f) => (
                  <TableRow key={f.name} data-testid={`founder-row-${f.name}`}>
                    <TableCell className="font-medium text-sm">{f.name}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{f.count || "—"}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums text-[#DC2626]">{f.paid_eur ? `−${fmtNum(f.paid_eur)}` : "—"}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums text-[#0D9488]">{f.received_eur ? `+${fmtNum(f.received_eur)}` : "—"}</TableCell>
                    <TableCell className="text-right text-sm text-[#475569]">{f.last_date ? fmtDate(f.last_date) : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-sm p-5" data-testid="internal-transfers-table">
            <h2 className="font-heading text-lg font-semibold text-[#0F172A] mb-1">Transferi između računa</h2>
            <p className="text-xs text-[#64748B] mb-3" data-testid="transfers-totals">
              Prebacivanja između vlastitih VTZ računa — ukupno {internal.transfers.count}
              {internal.transfers.count > 0 ? ` (uplate +${fmtNum(internal.transfers.in_eur, "€")} · isplate −${fmtNum(internal.transfers.out_eur, "€")})` : ""}.
            </p>
            {internal.transfers.items.length === 0 ? (
              <p className="text-sm text-[#64748B] py-4">Nema transfera za odabrano razdoblje.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F1F5F9] hover:bg-[#F1F5F9]">
                    <TableHead>Datum</TableHead>
                    <TableHead>Banka</TableHead>
                    <TableHead>Smjer</TableHead>
                    <TableHead className="text-right">Iznos</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {internal.transfers.items.map((t, i) => (
                    <TableRow key={i} data-testid={`transfer-row-${i}`}>
                      <TableCell className="text-sm tabular-nums whitespace-nowrap">{fmtDate(t.date)}</TableCell>
                      <TableCell className="text-sm">{t.bank}</TableCell>
                      <TableCell>
                        <Badge className={`rounded-sm text-xs font-medium hover:bg-inherit ${t.type === "naplata" ? "bg-[#0D9488]/10 text-[#0D9488]" : "bg-[#DC2626]/10 text-[#DC2626]"}`}>
                          {t.type === "naplata" ? "Uplata" : "Isplata"}
                        </Badge>
                      </TableCell>
                      <TableCell className={`text-right text-sm tabular-nums font-medium ${t.type === "naplata" ? "text-[#0D9488]" : "text-[#DC2626]"}`}>
                        {t.type === "naplata" ? "+" : "−"}{fmtNum(t.amount, t.currency === "EUR" ? "€" : "KM")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && closeDetail()}>
        <DialogContent data-testid="partner-detail-dialog" className="max-w-3xl rounded-sm max-h-[85vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-[#0F172A] flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#0D9488]" /> {detail.name}
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="border border-[#E2E8F0] rounded-sm p-3">
                  <p className="text-[10px] uppercase tracking-wider text-[#64748B] mb-1">Plaćeno ukupno</p>
                  <p className="font-heading font-bold text-[#DC2626] tabular-nums">{detail.paid_eur ? `−${fmtNum(detail.paid_eur, "€")}` : fmtNum(0, "€")}</p>
                </div>
                <div className="border border-[#E2E8F0] rounded-sm p-3">
                  <p className="text-[10px] uppercase tracking-wider text-[#64748B] mb-1">Naplaćeno ukupno</p>
                  <p className="font-heading font-bold text-[#0D9488] tabular-nums">{detail.received_eur ? `+${fmtNum(detail.received_eur, "€")}` : fmtNum(0, "€")}</p>
                </div>
                <div className="border border-[#E2E8F0] rounded-sm p-3">
                  <p className="text-[10px] uppercase tracking-wider text-[#64748B] mb-1">Transakcija</p>
                  <p className="font-heading font-bold text-[#0F172A] tabular-nums">{detail.count}</p>
                </div>
                <div className="border border-[#E2E8F0] rounded-sm p-3">
                  <p className="text-[10px] uppercase tracking-wider text-[#64748B] mb-1">Otvoreni planovi</p>
                  <p className={`font-heading font-bold tabular-nums ${detail.overdue_plans > 0 ? "text-[#DC2626]" : "text-[#0F172A]"}`}>
                    {detail.open_plans.length}{detail.overdue_plans > 0 ? ` (${detail.overdue_plans} dospjelo)` : ""}
                  </p>
                </div>
              </div>

              {detail.avg_delay_days !== null && (
                <div data-testid="partner-delay-insight" className={`flex items-start gap-2 rounded-sm px-4 py-3 text-sm ${detail.avg_delay_days > 2 ? "bg-[#D97706]/10 border border-[#D97706]/30" : "bg-[#0D9488]/5 border border-[#0D9488]/20"}`}>
                  <Clock className={`w-4 h-4 shrink-0 mt-0.5 ${detail.avg_delay_days > 2 ? "text-[#D97706]" : "text-[#0D9488]"}`} />
                  <p className="text-[#0F172A]">
                    Ovaj partner obično {detail.avg_delay_days > 0 ? `plati ${detail.avg_delay_days} dana NAKON planiranog datuma` : detail.avg_delay_days < 0 ? `plati ${Math.abs(detail.avg_delay_days)} dana PRIJE planiranog datuma` : "plaća točno na vrijeme"}
                    <span className="text-[#475569]"> (na temelju {detail.delay_count} uparenih transakcija)</span> — uzmite to u obzir kod planiranja.
                  </p>
                </div>
              )}

              <div>
                <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B] mb-2">Trend {detail.year}. (EUR)</p>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} barGap={2}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => fmtNum(v, "€")} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="Naplaćeno" fill="#0D9488" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="Plaćeno" fill="#DC2626" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {detail.open_plans.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#D97706] mb-2">Otvoreni planovi</p>
                  <div className="space-y-1.5">
                    {detail.open_plans.map((p) => (
                      <div key={p.id} className="flex justify-between text-sm border border-dashed border-[#D97706]/40 rounded-sm px-3 py-1.5">
                        <span>{fmtDate(p.date)} · {p.category}</span>
                        <span className="tabular-nums font-medium">{p.type === "naplata" ? "+" : "−"}{fmtNum(p.amount, p.currency)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B] mb-2">Zadnje transakcije</p>
                <div className="space-y-1">
                  {detail.recent.map((t) => (
                    <div key={t.id} className="flex justify-between text-sm border-b border-[#F1F5F9] py-1.5">
                      <span className="text-[#475569]">{fmtDate(t.date)} · {t.category}</span>
                      <span className={`tabular-nums font-medium ${t.type === "naplata" ? "text-[#0D9488]" : "text-[#DC2626]"}`}>
                        {t.type === "naplata" ? "+" : "−"}{fmtNum(t.amount, t.currency)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
